'use client';

import { useEffect } from 'react';

/**
 * Ajustes del widget de chatbot embebido (buildmyagent), que vive en un shadow
 * DOM ABIERTO y se fuerza a `z-index: 2147483647` (el máximo). Como el shadow es
 * abierto, entramos por JS a hacer dos cosas:
 *
 * 1. Espaciado: el widget se ancla pegado a la esquina inferior derecha
 *    (`bottom:0; right:0`). Lo empujamos GAP px hacia adentro, reafirmándolo
 *    cuando el widget reescribe su posición (init/resize/abrir-cerrar). Solo
 *    desktop; en mobile usa un overlay fullscreen con su propio inset.
 *
 * 2. No tapar modales: con su z-index máximo, el widget quedaría encima de la
 *    galería de vehículos y cualquier modal de la app (todos HeroUI, que
 *    renderizan `[aria-modal="true"]` + `[data-slot="backdrop"]`). Mientras haya
 *    uno abierto lo ocultamos y lo restauramos al cerrarse. OJO: NO sirve
 *    `display:none` en el host — el shadow del widget trae una regla
 *    `:host { ... !important }` y, por las reglas de encapsulación, una regla
 *    `:host` important del inner tree le gana al `style` inline del host. Por eso
 *    ocultamos con `opacity:0 + pointer-events:none` (que `:host` no fuerza).
 *
 * Se monta solo cuando hay `chatbot_widget_url` (ver TrackingAndConsent).
 */

// Separación (px) entre el widget y las esquinas del viewport. Ajustable acá.
const GAP = '24px';

const HOST_SELECTOR = '[id^="ai-chat-widget-host-"]';
const CONTAINER_ID = 'ai-chat-widget-container';
// Un modal/galería nuestro (HeroUI) está abierto → el chatbot no debe taparlo.
const MODAL_SELECTOR = '[aria-modal="true"], [data-slot="backdrop"]';

export default function ChatbotWidgetSpacing() {
  useEffect(() => {
    let containerObserver: MutationObserver | null = null;
    let domObserver: MutationObserver | null = null;
    let containerWired = false;

    const getHost = () =>
      document.querySelector(HOST_SELECTOR) as HTMLElement | null;

    const applyGap = (container: HTMLElement) => {
      if (window.innerWidth <= 768) return; // mobile: overlay propio, no tocar
      // Guard anti-loop: si ya está en su lugar, no reescribir.
      if (container.style.bottom === GAP && container.style.right === GAP) return;
      container.style.setProperty('bottom', GAP, 'important');
      container.style.setProperty('right', GAP, 'important');
    };

    const wireContainer = (): boolean => {
      const shadow = getHost()?.shadowRoot;
      if (!shadow) return false;
      const container = shadow.getElementById(CONTAINER_ID) as HTMLElement | null;
      if (!container) return false;
      applyGap(container);
      containerObserver?.disconnect();
      containerObserver = new MutationObserver(() => applyGap(container));
      containerObserver.observe(container, {
        attributes: true,
        attributeFilter: ['style'],
      });
      return true;
    };

    const syncModalVisibility = () => {
      const host = getHost();
      if (!host) return;
      const modalOpen = !!document.querySelector(MODAL_SELECTOR);
      if (modalOpen) {
        // Re-asserción idempotente (el widget re-appendea su host seguido).
        if (host.style.opacity !== '0') {
          host.style.setProperty('opacity', '0', 'important');
          host.style.setProperty('pointer-events', 'none', 'important');
        }
      } else if (host.style.opacity === '0') {
        host.style.removeProperty('opacity');
        host.style.removeProperty('pointer-events');
      }
    };

    const tick = () => {
      if (!containerWired) containerWired = wireContainer();
      syncModalVisibility();
    };

    tick();
    // El widget monta async y los modales se abren/cierran como portales en el
    // body: un solo observer cubre ambos. Es childList-only (se dispara al
    // agregar/quitar nodos, no en animaciones de estilo) y MutationObserver ya
    // agrupa por microtask, así que llamar tick() directo es barato.
    domObserver = new MutationObserver(tick);
    domObserver.observe(document.body, { childList: true, subtree: true });

    const onResize = () => {
      containerWired = wireContainer() || containerWired;
    };
    window.addEventListener('resize', onResize);

    return () => {
      containerObserver?.disconnect();
      domObserver?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return null;
}
